import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, CalendarClock, Users, Sparkles, AlertTriangle, ScrollText, Pencil,
  Plus, Phone, Mail, Trash2, CheckCircle2, CalendarPlus, ArrowUpRight, MessageCircle,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn, initialsOf } from "@/lib/utils";
import { Card, Row } from "@/components/placements/ContextTabs";
import { fmtDateFr, relAgo } from "@/components/placements/placementsShared";
import { useLang } from "@/i18n";

export function SituationCard({ detail, onRefresh }) {
  const { tf } = useLang();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const isOpen = detail.status === "OPEN";
  const save = async () => {
    try {
      await api.patch(`/discussions/${detail.id}`, { current_summary: draft });
      toast.success(tf("Situation mise à jour"));
      setEditing(false); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  return (
    <Card title={tf("Situation actuelle")} Icon={FileText} testid="card-situation"
      action={isOpen && (
        <button onClick={() => { setDraft(detail.current_summary || ""); setEditing((v) => !v); }}
          className="text-[11px] text-info hover:underline" data-testid="situation-edit">
          {editing ? tf("Fermer") : detail.current_summary ? tf("Modifier") : tf("Rédiger")}
        </button>)}>
      {editing ? (
        <div className="space-y-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} className="min-h-[96px] text-sm"
            placeholder={tf("Résumé rédigé par l'agent : où en est la discussion, ce qui est prévu…")}
            data-testid="situation-input" />
          <Button size="sm" onClick={save} className="h-8" data-testid="situation-save">{tf("Enregistrer")}</Button>
        </div>
      ) : detail.current_summary ? (
        <p className="text-sm leading-relaxed" data-testid="situation-text">{detail.current_summary}</p>
      ) : (
        <p className="text-sm text-muted-foreground">{tf("Aucun résumé rédigé pour cette discussion.")}</p>
      )}
    </Card>
  );
}

export function NextActionCard({ detail, onRefresh, onPlan }) {
  const { tf } = useLang();
  const [busy, setBusy] = useState(false);
  const na = detail.next_action;
  const isOpen = detail.status === "OPEN";
  const overdue = na?.days != null && na.days < 0;
  const complete = async () => {
    if (busy || !na) return;
    setBusy(true);
    try {
      await api.post(`/discussions/${detail.id}/actions/${na.id}/complete`);
      toast.success(tf("Action réalisée"));
      onRefresh?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };
  const toCalendar = async () => {
    if (busy || !na) return;
    setBusy(true);
    try {
      const r = await api.post(`/discussions/${detail.id}/actions/${na.id}/calendar`);
      toast.success(r.data.already ? tf("Déjà au calendrier") : tf("Ajouté au calendrier"));
      onRefresh?.();
    } catch { toast.error(tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Card title={tf("Prochaine action")} Icon={CalendarClock} testid="card-next-action"
      action={na && (
        <span className={cn("text-[10px] font-medium rounded-full border px-2 py-0.5",
          overdue ? "bg-athlynx/10 text-athlynx border-athlynx/30" : "bg-warning/10 text-warning border-warning/30")}>
          {overdue ? tf("En retard") : tf("À faire")}
        </span>)}>
      {na ? (
        <>
          <div className="text-sm font-semibold" data-testid="next-action-note">{na.note || tf("Suivi")}</div>
          <div className={cn("text-xs mt-1", overdue ? "text-athlynx" : "text-muted-foreground")}>
            {fmtDateFr(na.due_at)}{na.days != null && (overdue
              ? ` · ${tf("En retard de")} ${Math.abs(na.days)} j`
              : ` · ${relAgo(na.due_at)}`)}
          </div>
          {isOpen && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" onClick={complete} disabled={busy} className="h-8 gap-1.5 bg-info hover:bg-info/90 text-white"
                data-testid="next-action-complete">
                <CheckCircle2 className="h-3.5 w-3.5" /> {tf("Marquer comme réalisée")}
              </Button>
              <Button size="sm" variant="outline" onClick={toCalendar} disabled={busy || !!na.calendar_event_id}
                className="h-8 gap-1.5 border-border dark:border-white/10" data-testid="next-action-calendar">
                <CalendarPlus className="h-3.5 w-3.5" /> {na.calendar_event_id ? tf("Au calendrier") : tf("Ajouter au calendrier")}
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{tf("Aucune prochaine action planifiée.")}</p>
          {isOpen && (
            <Button size="sm" variant="outline" onClick={onPlan} className="mt-3 h-8 gap-1.5 border-border dark:border-white/10"
              data-testid="plan-action-btn">
              <Plus className="h-3.5 w-3.5" /> {tf("Planifier une action")}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}

export function InterlocutorsCard({ detail, onRefresh, onAdd }) {
  const { tf } = useLang();
  const isOpen = detail.status === "OPEN";
  const a = detail.athlete || {};
  const list = detail.interlocutors || [];
  const remove = async (cid) => {
    try { await api.delete(`/discussions/${detail.id}/interlocutors/${cid}`); onRefresh?.(); }
    catch { toast.error(tf("Erreur")); }
  };
  const Person = ({ name, role, phone, email, onRemove, testid }) => (
    <li className="flex items-center justify-between gap-2" data-testid={testid}>
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="h-8 w-8 rounded-full bg-secondary grid place-items-center text-[10px] font-semibold shrink-0">
          {initialsOf(name)}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{name}</div>
          <div className="text-xs text-muted-foreground truncate">{role}</div>
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        {phone && <a href={`tel:${phone}`} aria-label={tf("Appeler")} className="p-1.5 rounded-md text-info hover:bg-info/10"><Phone className="h-3.5 w-3.5" /></a>}
        {phone && <a href={`https://wa.me/${String(phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="p-1.5 rounded-md text-success hover:bg-success/10"><MessageCircle className="h-3.5 w-3.5" /></a>}
        {email && <a href={`mailto:${email}`} aria-label={tf("Envoyer un email")} className="p-1.5 rounded-md text-info hover:bg-info/10"><Mail className="h-3.5 w-3.5" /></a>}
        {onRemove && <button onClick={onRemove} aria-label={tf("Retirer")} className="p-1.5 text-muted-foreground/40 hover:text-athlynx"><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </li>
  );
  return (
    <Card title={tf("Interlocuteurs")} Icon={Users} testid="card-interlocutors"
      action={isOpen && (
        <button onClick={onAdd} className="text-[11px] text-info hover:underline inline-flex items-center gap-0.5"
          data-testid="interlocutor-add-btn">
          <Plus className="h-3 w-3" /> {tf("Ajouter")}
        </button>)}>
      <ul className="space-y-2.5">
        <Person name={a.name} role={tf("Athlète")} phone={detail.athlete_phone} email={detail.athlete_email}
          testid="interlocutor-athlete" />
        {list.map((c) => (
          <Person key={c.id} name={c.name} role={tf(c.role)} phone={c.phone} email={c.email}
            onRemove={isOpen ? () => remove(c.id) : undefined} testid={`interlocutor-${c.id}`} />
        ))}
      </ul>
      {list.length === 0 && (
        <p className="text-xs text-muted-foreground mt-2.5">{tf("Aucun autre interlocuteur ajouté.")}</p>
      )}
    </Card>
  );
}

export function ItemsCard({ detail, type, title, Icon, emptyText, addPlaceholder, onRefresh, testid, dotCls }) {
  const { tf } = useLang();
  const [adding, setAdding] = useState(false);
  const [content, setContent] = useState("");
  const isOpen = detail.status === "OPEN";
  const items = (type === "EXPECTATION" ? detail.expectations : detail.attention_points) || [];
  const open = items.filter((i) => i.status !== "resolved");
  const add = async () => {
    if (!content.trim()) return;
    try {
      await api.post(`/discussions/${detail.id}/items`, { type, content });
      setContent(""); setAdding(false); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  const resolve = async (iid) => {
    try { await api.patch(`/discussions/${detail.id}/items/${iid}`, { status: "resolved" }); onRefresh?.(); }
    catch { toast.error(tf("Erreur")); }
  };
  const remove = async (iid) => {
    try { await api.delete(`/discussions/${detail.id}/items/${iid}`); onRefresh?.(); }
    catch { toast.error(tf("Erreur")); }
  };
  return (
    <Card title={title} Icon={Icon} testid={testid}
      action={isOpen && (
        <button onClick={() => setAdding((v) => !v)} className="text-[11px] text-info hover:underline inline-flex items-center gap-0.5"
          data-testid={`${testid}-add-toggle`}>
          <Plus className="h-3 w-3" /> {tf("Ajouter")}
        </button>)}>
      {adding && (
        <div className="mb-3 flex gap-2">
          <input value={content} onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder={addPlaceholder}
            className="flex-1 min-w-0 h-9 rounded-lg border border-border dark:border-white/10 bg-background/60 px-2.5 text-xs outline-none focus:border-info/50"
            data-testid={`${testid}-input`} />
          <Button size="sm" onClick={add} className="h-9 shrink-0" data-testid={`${testid}-save`}>{tf("OK")}</Button>
        </div>
      )}
      {open.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {open.map((it) => (
            <li key={it.id} className="flex items-start justify-between gap-2 text-sm group" data-testid={`item-${it.id}`}>
              <span className="flex items-start gap-2 min-w-0">
                <span className={cn("h-1.5 w-1.5 rounded-full mt-1.5 shrink-0", dotCls)} />
                <span className="min-w-0 break-words">{it.content}</span>
              </span>
              {isOpen && (
                <span className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => resolve(it.id)} title={tf("Marquer comme traité")}
                    className="p-1 text-muted-foreground/50 hover:text-success" data-testid={`item-resolve-${it.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => remove(it.id)} title={tf("Supprimer")}
                    className="p-1 text-muted-foreground/50 hover:text-athlynx" data-testid={`item-del-${it.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ExpectationsCard(props) {
  const { tf } = useLang();
  return <ItemsCard {...props} type="EXPECTATION" title={tf("Attentes de l'athlète")} Icon={Sparkles}
    emptyText={tf("Aucune attente renseignée.")} addPlaceholder={tf("Ex. : Évoluer en Ligue 2 d'ici 2 saisons")}
    testid="card-expectations" dotCls="bg-info" />;
}

export function AttentionPointsCard(props) {
  const { tf } = useLang();
  return <ItemsCard {...props} type="ATTENTION_POINT" title={tf("Points d'attention / objections")} Icon={AlertTriangle}
    emptyText={tf("Aucun point d'attention enregistré.")} addPlaceholder={tf("Ex. : Souhaite comparer plusieurs propositions")}
    testid="card-attention" dotCls="bg-warning" />;
}

export function TermsCard({ detail, onEdit }) {
  const { tf } = useLang();
  const t = detail.proposed_terms;
  const isOpen = detail.status === "OPEN";
  return (
    <Card title={tf("Conditions envisagées")} Icon={ScrollText} testid="card-terms"
      action={isOpen && (
        <button onClick={onEdit} className="text-[11px] text-info hover:underline inline-flex items-center gap-0.5"
          data-testid="terms-edit-btn">
          <Pencil className="h-3 w-3" /> {t ? tf("Modifier") : tf("Ajouter")}
        </button>)}>
      {t && Object.values(t).some(Boolean) ? (
        <>
          <Row label={tf("Type de mandat")} value={t.mandate_type} />
          <Row label={tf("Durée")} value={t.duration} />
          <Row label={tf("Commission")} value={t.commission} />
          <Row label={tf("Services")} value={t.services} />
          {t.territory && <Row label={tf("Territoire")} value={t.territory} />}
          {t.exclusivity && <Row label={tf("Exclusivité")} value={t.exclusivity} />}
          <Row label={tf("Statut")} value={t.status} />
          {t.notes && <p className="text-xs text-muted-foreground mt-2">{t.notes}</p>}
          <p className="text-[10px] text-muted-foreground/60 mt-2.5">
            {tf("Conditions en discussion — distinctes du contrat final.")}
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="terms-empty">{tf("Aucune condition renseignée.")}</p>
      )}
    </Card>
  );
}

export function ContractContextCard({ detail }) {
  const { tf } = useLang();
  const c = detail.contract;
  const r = detail.renewal;
  const a = detail.athlete || {};
  return (
    <Card title={tf("Situation contractuelle")} Icon={ScrollText} testid="card-contract-context">
      <Row label={tf("Club actuel")} value={a.club} />
      {c ? (
        <>
          <Row label={tf("Fin de contrat")} value={c.end_date ? fmtDateFr(c.end_date) : null}
            tone={c.days_left != null && c.days_left <= 240 ? "text-warning" : undefined} />
          {c.days_left != null && c.days_left >= 0 && <Row label={tf("Temps restant")} value={`${c.days_left} ${tf("jours")}`} />}
          <Row label={tf("Statut du contrat")} value={c.status} />
        </>
      ) : (
        <Row label={tf("Fin de contrat")} value={null} />
      )}
      {r && <Row label={tf("Renouvellement")} value={r.status_label || r.status} />}
      {c && (
        <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-white/10">
          <Link to={`/contracts/${c.id}`} data-testid="view-contract-link">
            {tf("Voir le contrat")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      )}
    </Card>
  );
}
